import React from 'react';
import styled from '@emotion/styled';
import { theme, SectionWrapper as SectionWrapperStyles } from '@styles';
import { ClubB2BSectionData } from './types';
import { OUTER_SPACE, MOUNTAIN_MEADOW, WHITE, BLACK_HAZE } from '@constants';
import Button from '@components/Button';
import CheckIcon from '@assets/icons/CheckIcon';

const { breakpoints } = theme;

const ClubB2BSection: React.FC<ClubB2BSectionData> = (props) => {
  const { b2b_title, b2b_description, b2b_points, b2b_cta_text, b2b_button_link, b2b_mockup } = props;

  // Follow existing error handling pattern
  if (!b2b_title || !b2b_description) {
    console.error('Club B2B section: Missing required Prismic data', {
      has_title: !!b2b_title,
      has_description: !!b2b_description,
      has_points: !!b2b_points,
      points_count: b2b_points?.length || 0
    });
    return null;
  }

  const handleCtaClick = () => {
    const targetUrl = b2b_button_link || '/book-a-demo';
    window.location.href = targetUrl;
  };

  return (
    <SectionWrapper id="b2b-section">
      <ContentWrapper>
        <ContentGrid>
          <TextContent>
            <SectionTitle>{b2b_title}</SectionTitle>
            <SectionDescription>{b2b_description}</SectionDescription>

            {b2b_points && b2b_points.length > 0 && (
              <PointsList>
                {b2b_points.map((point, index) => (
                  <PointItem key={index}>
                    <PointIcon>✓</PointIcon>
                    <PointText>
                      {typeof point === 'object' && 'point_text' in point
                        ? point.point_text
                        : point as string}
                    </PointText>
                  </PointItem>
                ))}
              </PointsList>
            )}

            {b2b_cta_text && (
              <CTAWrapper>
                <Button
                  label={b2b_cta_text}
                  type="primary"
                  height={56}
                  onClick={handleCtaClick}
                />
              </CTAWrapper>
            )}
          </TextContent>

          <VisualContent>
            {typeof b2b_mockup === 'object' && b2b_mockup && 'url' in b2b_mockup && b2b_mockup.url && (
              <MockupContainer>
                <img
                  src={b2b_mockup.url}
                  alt={b2b_mockup.alt || 'Branded app mockup'}
                />
              </MockupContainer>
            )}
          </VisualContent>
        </ContentGrid>
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default ClubB2BSection;

const SectionWrapper = styled(SectionWrapperStyles)`
  padding: 100px 24px;
  background: ${BLACK_HAZE};
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    padding: 140px 24px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  align-items: center;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    grid-template-columns: 1fr 1fr;
    gap: 80px;
  }
`;

const TextContent = styled.div`
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    order: 1;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: ${OUTER_SPACE};
  margin-bottom: 24px;
  font-family: 'Poppins', sans-serif;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 42px;
    line-height: 50px;
    margin-bottom: 32px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 28px;
  color: ${OUTER_SPACE};
  opacity: 0.8;
  margin-bottom: 40px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 20px;
    line-height: 30px;
    margin-bottom: 48px;
  }
`;

const PointsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
`;

const PointItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    margin-bottom: 24px;
  }
`;

const PointIcon = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${MOUNTAIN_MEADOW};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  margin-top: 2px;
  
  svg {
    color: ${WHITE};
  }
`;

const PointText = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: ${OUTER_SPACE};
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 17px;
    line-height: 26px;
  }
`;

const CTAWrapper = styled.div`
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    margin-top: 56px;
  }
  
  button {
    width: 100%;
    max-width: 300px;
  }
`;

const VisualContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    order: 2;
  }
`;

const MockupContainer = styled.div`
  max-width: 350px;
  width: 100%;
  
  img {
    width: 100%;
    height: auto;
    filter: drop-shadow(0px 20px 40px rgba(0, 0, 0, 0.15));
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    max-width: 400px;
  }
`; 